import appConfig from 'app-config';
import { fromRuntime } from 'runtime-config';
import { getBrand, getBrandKey, getBrandLogo } from 'flow/brands/brands';
import EmbeddedType from 'flow/types/embedded-type';
import { routes } from 'flow/types/routes';
import { isEmptyNullOrUndefined } from 'flow/utils';
import DOMPurify from 'dompurify';
import {
    electronBrandKeys,
    isFlowBrand,
    isJscBrand,
    isTelavoxBrand,
} from 'flow/utils/brand-utils';
import { IS_UAE_HOST } from './browser-utils-TW';

export const RELEASE_NOTES = 'Release notes'; //For now the notes should be all in English so no translation needed.

export const isMobileApp = (embeddedType) =>
    embeddedType === EmbeddedType.IOS || embeddedType === EmbeddedType.ANDROID;

export const isEmbeddedTypeCti = (embeddedType) => {
    return EmbeddedType.INTEGRATION.is(embeddedType);
};

export const isTeamsTypeIntegration = (embeddedType) => {
    return EmbeddedType.TEAMS.is(embeddedType);
};

export const isTeamsFullVersion = (embeddedType) => {
    return EmbeddedType.TEAMSFV.is(embeddedType);
};

export const isTeamsFullManualLogin = (embeddedType) => {
    return EmbeddedType.TEAMSFVMANUAL.is(embeddedType);
};

/**
 * TEAMSFV and TEAMSFVMANUAL both run the complete client (softphone, feature
 * flags, ringtones, ...) inside a Teams tab. They differ only in how the user
 * authenticates: TEAMSFV does silent SSO through teams-js, TEAMSFVMANUAL uses
 * our own sign-in form. Prefer this over isTeamsFullVersion for anything that
 * isn't about the authentication flow.
 */
export const isTeamsFullClient = (embeddedType) =>
    isTeamsFullVersion(embeddedType) || isTeamsFullManualLogin(embeddedType);

export const isTeamsAnyVersion = (embeddedType) =>
    isTeamsTypeIntegration(embeddedType) || isTeamsFullClient(embeddedType);

/**
 * e& (UAE) runs the Teams full client purely as a call surface — the companion
 * desktop app owns the media, so Messages and PBX services are noise there and
 * the slimmed-down variant drops those tabs.
 *
 * Only the full client is affected: the stripped TEAMS embed never renders a
 * tab bar to begin with (AppToolbar skips ToolbarMenu for it).
 *
 * To exercise this locally, point a dev build at the UAE host and sign in with
 * an e& account: the region is derived from the host, not the user, so a dev
 * build against a Telavox backend is deliberately unaffected.
 */
export const isSlimmedTeamsApp = (embeddedType) =>
    IS_UAE_HOST && isTeamsFullClient(embeddedType);

// The slimmed-down Teams app hides Messages and PBX, so every entry point lands here.
export const getSlimmedTeamsLandingRoute = () => routes['extensions'];

export function isAppCti(state) {
    const { app } = state;
    const embedded = app?.get('embedded');
    return isEmbeddedTypeCti(embedded);
}

export const getBundleFilename = () => {
    const currentScriptSource =
        document && document.currentScript && document.currentScript.src;
    return currentScriptSource
        ? /.*\/(.*\.js)$/.exec(currentScriptSource)[1]
        : null;
};

export const permissionNotificationsGranted = (inWindow, notification) => {
    if (!inWindow) {
        return false;
    } else {
        const permissionSuccess =
            inWindow && notification.permission === 'granted';
        if (!inWindow || !permissionSuccess) {
            return false;
        }
    }
    return true;
};

export const isBeta = () => appConfig.environment === 'beta';
export const isDevelopment = () => appConfig.environment === 'dev';
export const isBetaOrDevEnvironment = () => isBeta() || isDevelopment();

export const getClientType = (isElectron) =>
    isElectron ? 'flow_desktop' : 'flowapp';

const FLOW_LINKS = {
    android:
        'https://play.google.com/store/apps/details?id=com.telavox.android.flow',
    ios: 'https://apps.apple.com/se/app/flow-uc/id1504435678',
    windows: 'https://joinflow.com/en/apps/windows/',
    mac: 'https://joinflow.com/en/apps/osx/',
    website: 'https://www.joinflow.com/apps',
};

export const getAppDownloadUrls = () => {
    const brandKey = getBrandKey();

    if (!brandKey || IS_UAE_HOST) {
        return null; // Don't show links if we are using the fallback brand or UAE host
    }

    if (isTelavoxBrand(brandKey)) {
        return {
            android:
                'https://play.google.com/store/apps/details?id=se.telavox.android.otg',
            ios: 'https://itunes.apple.com/us/app/telavox/id302899039?mt=8',
            windows: 'https://telavox.com/apps/',
            mac: 'https://telavox.com/apps/',
            website: 'https://www.telavox.com/apps',
        };
    }
    if (isFlowBrand(brandKey)) {
        return FLOW_LINKS;
    }

    const electronRetailer = Object.keys(electronBrandKeys).find(
        (key) => electronBrandKeys[key] === brandKey,
    );

    if (isJscBrand(brandKey)) {
        return FLOW_LINKS;
    }

    //Do not show download links for electron brands
    if (electronRetailer) {
        return null;
    }

    // Retailers with no electron app are fine with
    // the default Flow download links
    return FLOW_LINKS;
};

export const RightPaneWidth = 360;

export const setTitleAndFavIcon = () => {
    const withEnvironmentBranding = true;

    const brand = getBrand(withEnvironmentBranding);
    document.title = brand.brandNameUIFriendly || '';

    const getFavicon = () => {
        const darkMode = window.matchMedia?.(
            '(prefers-color-scheme: dark)',
        ).matches;
        return darkMode &&
            getBrandLogo('faviconDarkMode', withEnvironmentBranding)
            ? getBrandLogo('faviconDarkMode', withEnvironmentBranding)
            : getBrandLogo('favicon', withEnvironmentBranding);
    };

    const favicon = getFavicon();

    // Only update favicon if one is available
    if (favicon) {
        const updateFavicon = (id) => {
            const faviconElement = document.getElementById(id);
            if (faviconElement) faviconElement.href = favicon;
        };

        ['favicon', 'safari_favicon', 'apple_touch_favicon'].forEach(
            updateFavicon,
        );
    }
};

export const calculateSelectedTab = (location) => {
    const pathArray = location?.pathname?.split(/[/?]/);

    if (!pathArray) {
        return null;
    }

    if (isEmptyNullOrUndefined(pathArray[1])) {
        return null;
    }

    if (pathArray[1] === 'video') {
        return 'messages';
    }

    return pathArray[1];
};

export const isMobileDevice = () => {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

export const isHTMLPage = (content) =>
    /<!doctype html>|<html[\s>]/i.test(content);

export const APPSTORE_ENTRY_ID = {
    ZENDESK_CALL_LOGGING: 'appstoreentry-ipaas-278',
};

export const sanitizeHTML = (dirty) =>
    DOMPurify.sanitize(dirty, {
        ALLOWED_TAGS: [
            'p',
            'br',
            'b',
            'strong',
            'i',
            'em',
            'ul',
            'ol',
            'li',
            'div',
            'span',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
        ],
        ALLOWED_ATTR: [],
    });

export const regionCheckEndpoint = fromRuntime('regionGeonexService');

/**
 * Enables the region dropdown + username→region lookup UI (CTI web).
 * Separate from regionCheckEndpoint (Gate 1 bootstrap routing) so region UI
 * can be turned on independently of where the first branding request lands.
 */
export const regionLookupEnabled = !!fromRuntime('regionLookupEnabled');
